import { EventSubscriber, ReplicaType, } from '@verdant-web/common';
import { HANDLE_MESSAGE, PresenceManager } from './PresenceManager.js';
import { FileSync } from './FileSync.js';
import { PushPullSync } from './PushPullSync.js';
import { ServerSyncEndpointProvider, } from './ServerSyncEndpointProvider.js';
import { WebSocketSync } from './WebSocketSync.js';
export class NoSync extends EventSubscriber {
    constructor() {
        super(...arguments);
        this.mode = 'pull';
        this.dispose = () => { };
        this.isConnected = false;
        this.status = 'paused';
        this.pullInterval = 0;
        this.presence = new PresenceManager({
            initialPresence: {},
            defaultProfile: {},
        });
        this.uploadFile = async () => {
            return {
                success: false,
                retry: false,
            };
        };
        this.getFile = async () => {
            return {
                success: false,
                retry: false,
            };
        };
    }
    send() { }
    start() { }
    stop() { }
    reconnect() { }
    setMode() { }
    setPullInterval() { }
}
export class ServerSync extends EventSubscriber {
    constructor({ authEndpoint, fetchAuth, initialPresence, automaticTransportSelection = true, autoStart, initialTransport, pullInterval, presenceUpdateBatchTimeout, defaultProfile, }, { meta, log, onData, }) {
        super();
        this.handleMessage = async (message) => {
            // TODO: move this into metadata
            if (message.type === 'op-re' || message.type === 'sync-resp') {
                for (const op of message.operations) {
                    this.meta.time.update(op.timestamp);
                }
            }
            this.log('sync message', JSON.stringify(message, null, 2));
            switch (message.type) {
                case 'op-re':
                    await this.onData({
                        operations: message.operations,
                        baselines: message.baselines,
                    });
                    if (message.globalAckTimestamp) {
                        await this.meta.setGlobalAck(message.globalAckTimestamp);
                    }
                    break;
                case 'global-ack':
                    await this.meta.setGlobalAck(message.timestamp);
                    break;
                case 'sync-resp':
                    await this.onData({
                        operations: message.operations,
                        baselines: message.baselines,
                        reset: message.overwriteLocalData,
                    });
                    if (message.globalAckTimestamp) {
                        await this.meta.setGlobalAck(message.globalAckTimestamp);
                    }
                    await this.meta.updateLastSynced(message.ackedTimestamp);
                    break;
                case 'need-since':
                    this.activeSync.send(await this.meta.messageCreator.createSyncStep1(message.since));
                    break;
                case 'server-ack':
                    await this.meta.updateLastSynced(message.timestamp);
            }
            // update presence if necessary
            this.presence[HANDLE_MESSAGE](await this.meta.localReplica.get(), message);
        };
        this.handleOnlineChange = (online) => {
            this.emit('onlineChange', online);
        };
        this.handlePresenceUpdate = async (presence) => {
            this.send(await this.meta.messageCreator.createPresenceUpdate(presence));
        };
        this.setMode = (transport) => {
            if (transport === 'realtime' && !this.canDoRealtime) {
                throw new Error(`Cannot switch to realtime mode, because the current auth token does not allow it`);
            }
            let newSync;
            if (transport === 'realtime') {
                newSync = this.webSocketSync;
            }
            else {
                newSync = this.pushPullSync;
            }
            if (newSync === this.activeSync)
                return;
            this.log('switching to', transport, 'mode');
            // transfer state to new sync
            if (this.activeSync.status === 'active') {
                newSync.start();
            }
            this.activeSync.stop();
            this.activeSync = newSync;
        };
        this.setPullInterval = (interval) => {
            this.pushPullSync.setInterval(interval);
        };
        this.send = (message) => {
            if (this.activeSync.status === 'active') {
                return this.activeSync.send(message);
            }
        };
        this.uploadFile = async (info) => {
            if (this.activeSync.status === 'active') {
                return this.fileSync.uploadFile(info);
            }
            else {
                return {
                    success: false,
                    retry: false,
                };
            }
        };
        this.getFile = async (id) => {
            // TODO: should this error? or just try anyway?
            if (this.activeSync.status === 'active') {
                return this.fileSync.getFile(id);
            }
            else {
                throw new Error('Offline, cannot retrieve remote file details');
            }
        };
        this.start = () => {
            return this.activeSync.start();
        };
        this.stop = () => {
            return this.activeSync.stop();
        };
        this.dispose = () => {
            this.webSocketSync.dispose();
            this.pushPullSync.dispose();
        };
        this.reconnect = () => {
            return this.activeSync.reconnect();
        };
        this.meta = meta;
        this.onData = onData;
        this.log = log || (() => { });
        this.presence = new PresenceManager({
            initialPresence,
            defaultProfile,
            updateBatchTimeout: presenceUpdateBatchTimeout,
        });
        this.endpointProvider = new ServerSyncEndpointProvider({
            authEndpoint,
            fetchAuth,
        });
        this.webSocketSync = new WebSocketSync({
            endpointProvider: this.endpointProvider,
            meta,
            presence: this.presence,
            log: this.log,
        });
        this.pushPullSync = new PushPullSync({
            endpointProvider: this.endpointProvider,
            meta,
            presence: this.presence,
            log: this.log,
            interval: pullInterval,
        });
        this.fileSync = new FileSync({
            endpointProvider: this.endpointProvider,
            log: this.log,
        });
        if (initialTransport === 'realtime') {
            this.activeSync = this.webSocketSync;
        }
        else {
            this.activeSync = this.pushPullSync;
        }
        this.presence.subscribe('update', this.handlePresenceUpdate);
        this.meta.subscribe('message', this.send);
        this.webSocketSync.subscribe('message', this.handleMessage);
        this.webSocketSync.subscribe('onlineChange', this.handleOnlineChange);
        this.pushPullSync.subscribe('message', this.handleMessage);
        this.pushPullSync.subscribe('onlineChange', this.handleOnlineChange);
        if (automaticTransportSelection) {
            // automatically shift between transport modes depending
            // on whether any peers are present
            let switchoverTimeout;
            this.presence.subscribe('peersChanged', (peers) => {
                if (switchoverTimeout) {
                    clearTimeout(switchoverTimeout);
                }
                if (Object.keys(peers).length > 0) {
                    // only upgrade if token allows it
                    if (this.canDoRealtime) {
                        if (this.mode === 'pull') {
                            this.setMode('realtime');
                        }
                    }
                }
                else if (this.mode === 'realtime') {
                    // wait 1 second then switch to pull mode if still emtpy
                    switchoverTimeout = setTimeout(() => {
                        if (Object.keys(this.presence.peers).length === 0) {
                            this.setMode('pull');
                        }
                    }, 1000);
                }
            });
        }
        if (autoStart) {
            this.start();
        }
    }
    get canDoRealtime() {
        return (this.endpointProvider.type === ReplicaType.Realtime ||
            this.endpointProvider.type === ReplicaType.PassiveRealtime ||
            this.endpointProvider.type === ReplicaType.ReadOnlyRealtime);
    }
    get pullInterval() {
        return this.pushPullSync.interval;
    }
    get isConnected() {
        return this.activeSync.isConnected;
    }
    get status() {
        return this.activeSync.status;
    }
    get mode() {
        return this.activeSync.mode;
    }
}
//# sourceMappingURL=Sync.js.map